import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import MasterLayout from "./components/MasterLayout";
import Support from "./support";
import { useBrand } from "./context/BrandContext";
import { resolveBrandSupportContext } from "./brandSupportContext";
import { toClientSupportHubLocation } from "./supportHubPaths";
import { resolveAuthorizedBrandById } from "./brandSupportDeepLink";

/**
 * Brand-scoped Support (SUPPORT-QA-01) — `/master-account/support`.
 *
 * Entered from inside a selected Brand (brand shell). Renders Support in the
 * brand shell (MasterLayout) pinned to that Brand: Captain D's → Support is
 * Captain D's Support; McDonald's → Support is McDonald's Support.
 *
 * Email deep link (`?brandId=<id>&openTicket=KK-######`): the URL Brand is
 * presentation context only. It is honoured ONLY when it is one of the acting
 * user's authorized Brands (UserBrandAccess via /me/brands); then the brand
 * shell switches to it so the exact Case opens inside the right Brand. An
 * unauthorized/unknown Brand id falls back to the Client hub — the server
 * enforces the same boundary on every Support API call regardless.
 *
 * With no resolvable Brand context (zero-brand client, stale URL) it keeps
 * the prior behavior and redirects to the Client / Brand Manager hub.
 */
export default function BrandSupport() {
  const location = useLocation();
  const { currentBrand, updateBrand } = useBrand();
  const params = new URLSearchParams(location.search || "");
  const urlBrandId = String(params.get("brandId") || "").trim() || null;
  const currentBrandId = String(currentBrand?.id || "").trim() || null;
  const needsSwitch = Boolean(urlBrandId) && urlBrandId !== currentBrandId;

  // undefined = resolving, null = not applicable, false = not authorized
  const [urlBrand, setUrlBrand] = useState(needsSwitch ? undefined : null);

  useEffect(() => {
    if (!needsSwitch) {
      setUrlBrand(null);
      return undefined;
    }
    let cancelled = false;
    setUrlBrand(undefined);
    (async () => {
      const brand = await resolveAuthorizedBrandById(urlBrandId);
      if (cancelled) return;
      if (brand) {
        updateBrand({ id: brand.id, name: brand.name, logoUrl: brand.logoUrl });
      }
      setUrlBrand(brand || false);
    })();
    return () => {
      cancelled = true;
    };
  }, [needsSwitch, urlBrandId, updateBrand]);

  if (urlBrand === undefined) {
    return (
      <MasterLayout>
        <div className="no-tickets" data-testid="support-brand-resolving">
          Opening Support…
        </div>
      </MasterLayout>
    );
  }

  if (urlBrand === false) {
    // Not one of the user's Brands: never render another Brand's context.
    const hubParams = new URLSearchParams(location.search || "");
    hubParams.delete("brandId");
    const qs = hubParams.toString();
    return (
      <Navigate
        to={toClientSupportHubLocation({ search: qs ? `?${qs}` : "", hash: location.hash })}
        replace
      />
    );
  }

  const brandContext = urlBrand
    ? { brandId: urlBrand.id, brandName: urlBrand.name || urlBrand.id }
    : resolveBrandSupportContext({
        currentBrand,
        locationState: location.state,
      });

  if (!brandContext) {
    return <Navigate to={toClientSupportHubLocation(location)} replace />;
  }

  return (
    <MasterLayout>
      <Support brandContext={brandContext} />
    </MasterLayout>
  );
}
